import { useState } from "react";
import { Button, Card, CardBody } from "../../../components/ui";
import localization from "../../../config/localization";
import { ConfirmationModal } from "../../../components/ui/modals/ConfirmationModal";
import { TakeABreakRequest } from "../models/request";
import { localStorageService } from "../../../services";
import { responsibleGamingService } from "../service/responsibleGaming.service";
import { formatDateTime } from "../../../utils/dateTimeHelper";
import { toastService as toast } from "../../../components/ui/toast/toast.service";
import { FeedbackModal as SuccessModal } from "../../../components/ui/modals/FeedbackModal";
import { useNavigate } from "react-router-dom";
import userDeviceStore from "../../../store/device.store";
import { DeviceType } from "../../../models/constants/deviceType";

export function TakeABreak() {
  const timePeriods = [
    { id: "1 Day", label: localization["1 day"] },
    { id: "7 Days", label: localization["7 day"] },
    { id: "1 Month", label: localization["1 month"] },
    { id: "6 Weeks", label: localization["6 weeks"] },
  ];

  const deviceType = userDeviceStore((state) => state.deviceType);

  let takeABreakDuration = new Date();

  const navigate = useNavigate();

  const [selectedTimePeriod, setSelectedTimePeriod] = useState("");

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const handleTakeABreak = async () => {
    switch (selectedTimePeriod) {
      case "1 Day":
        takeABreakDuration.setDate(takeABreakDuration.getDate() + 1);
        break;
      case "7 Days":
        takeABreakDuration.setDate(takeABreakDuration.getDate() + 7);
        break;
      case "1 Month":
        takeABreakDuration.setMonth(takeABreakDuration.getMonth() + 1);
        break;
      case "6 Weeks":
        takeABreakDuration.setDate(takeABreakDuration.getDate() + 6 * 7);
        break;
    }

    const request: TakeABreakRequest = {
      id: localStorageService.getItem<string>("userId") ?? "",
      isTakeABreak: true,
      takeABreakDuration: selectedTimePeriod,
    };
    var response = await responsibleGamingService.takeABreak(request);
    if (response.isSuccess) {
      var message = localization.getString(response.message)
      toast.success(message);
      setIsConfirmationModalOpen(false);
      setIsSuccessModalOpen(true);
    } else {
      toast.error(response.message);
    }
  };

  const handleCloseSuccessModal = () => {
    localStorageService.clear();
    setIsSuccessModalOpen(false);
    navigate("/");
    window.location.reload();
  };

  const content = (
    <div className="px-4 pb-6">
      <div className="border border-[#333] rounded-lg p-4 bg-black mt-10">
        <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white">
          <img src={"/icons/ic-info.svg"} alt="Take a Break" className="w-8 h-8" />
        </div>

        <p className="mt-4  text-gray-300 leading-relaxed text-justify">
          {localization.takeabreaknotes}
        </p>
      </div>

      <div className="mt-6">
        <p className="text-sm text-gray-300 mb-3">
          {localization.time_period}
        </p>

        <div
          className="grid grid-cols-4 gap-3  lg:flex lg:justify-start lg:gap-4">
          {timePeriods.map((timePeriod) => {
            const isActive = selectedTimePeriod === timePeriod.id;

            return (
              <Button
                key={timePeriod.id}
                onClick={() => setSelectedTimePeriod(timePeriod.id)}
                className={`
            h-11
            rounded-md
            text-sm font-medium
            transition-all duration-200
            border border-[#4B4B4B]
            lg:px-6
            whitespace-nowrap
            ${isActive
                    ? "bg-purple-gradient-secondary text-white border-transparent"
                    : "text-gray-400 hover:text-white hover:border-gray-400"
                  }
          `}
              >
                {timePeriod.label}
              </Button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-center">
        <Button
          type="submit"
          className={`mt-6 w-full md:w-1/3 py-4 rounded-md bg-primary-gradient text-white font-semibold tracking-wide ${!selectedTimePeriod ? "opacity-50 cursor-not-allowed" : "hover:bg-primary-gradient/80 cursor-pointer"}`}
          onClick={() => setIsConfirmationModalOpen(true)}
          disabled={!selectedTimePeriod}
        >
          {localization.submit.toUpperCase()}
        </Button>
      </div>

      {isConfirmationModalOpen && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onConfirm={() => handleTakeABreak()}
          onCancel={() => setIsConfirmationModalOpen(false)}
          title={localization.takeabreakconfirmationlabel.replace(
            "{0}",
            timePeriods.find((tp) => tp.id === selectedTimePeriod)?.label ?? ""
          )}
          icon={"/icons/ic-info.svg"}
          blurColor="bg-[#FFE6CC]"
          confirmText={localization.yes.toUpperCase()}
          cancelText={localization.no.toUpperCase()}
        />
      )}

      {isSuccessModalOpen && (
        <SuccessModal
          isOpen={isSuccessModalOpen}
          title={localization.takeabreaksuccesslabel}
          description={formatDateTime(takeABreakDuration, false)}
          descriptionClassName="text-center text-lg font-normal text-[#111111]"
          icon={"/icons/ic-success-modal.svg"}
          blurColor="bg-[#D4FFDECC]"
          onClose={handleCloseSuccessModal}
        />
      )}
    </div>
  )

  return (
    deviceType !== DeviceType.Web ? content : (
      <Card>
        <CardBody showGradient>
          <div className="w-full max-w-5xl mx-auto">
            {content}
          </div>
        </CardBody>
      </Card>
    )

  );
}
